/**
 * Invitation-lifecycle status for a user. Persisted as `varchar(32)` — never a
 * Postgres ENUM (CLAUDE.md › Enums Over Literals), matching the `ProductStatus`
 * precedent.
 *
 * ORTHOGONAL to soft-delete: `deletedAt` + `isActive` remain the sole
 * delete/liveness axis, so this enum deliberately has no `DELETED` member
 * (TO-303 design D3 / gate answer 1). Provisioning writes `INVITED`; a
 * first-login that consumes an outstanding invitation moves it to `ACTIVE`
 * (TO-309).
 */
export enum UserStatus {
	INVITED = 'INVITED',
	ACTIVE = 'ACTIVE',
	SUSPENDED = 'SUSPENDED',
}

export interface IUser {
	/** Internal, app-owned id (primary key, globally unique). */
	userId: string
	/** The tenant this user belongs to (a user belongs to exactly one tenant). */
	tenantId: string
	/**
	 * External IdP subject (Cognito `sub`); null until federated. Unique per
	 * tenant (the same person signs in separately to each tenant).
	 */
	externalId?: string | null
	email: string
	displayName: string
	/** OIDC profile picture URL, imported from the IdP on first login. */
	picture?: string | null
	/**
	 * Invitation lifecycle. Defaults to `ACTIVE` for rows that predate TO-303;
	 * the provisioning path writes `INVITED` explicitly. Independent of
	 * `isActive`/`deletedAt`.
	 */
	status: UserStatus
	isActive: boolean
	/** When the user last made an authenticated request; null if never (TO-307). */
	lastActiveAt?: Date | null
	createdAt: Date
	updatedAt: Date
	deletedAt?: Date | null
}

/**
 * State of one invitation (`users_invitations.status`, TO-309). Persisted as
 * `varchar(32)`, never a Postgres ENUM. Expiry is not a status: it is derived
 * from `expiresAt` at read time.
 */
export enum InvitationStatus {
	/** Live: the link can still complete first-login (unless expired). */
	OUTSTANDING = 'OUTSTANDING',
	/** A first-login completed with it. */
	CONSUMED = 'CONSUMED',
	/** An inviter revoked it. */
	REVOKED = 'REVOKED',
	/** A re-send replaced it with a newer invitation. */
	SUPERSEDED = 'SUPERSEDED',
}

/** Where a completed first-login sends the user (TO-309 REQ-003). */
export enum FirstLoginDestination {
	/** A user who must enrol a TOTP factor before proceeding (TO-591); takes precedence over ONBOARDING/HOME. */
	ENROL_MFA = 'ENROL_MFA',
	/** A first-time admin: the tenant onboarding route. */
	ONBOARDING = 'ONBOARDING',
	/** Everyone else: the tenant home. */
	HOME = 'HOME',
}

/**
 * One first-login invitation (`users_invitations`, TO-309). Holds only the
 * SHA-256 hash of the emailed link token — never the token or the link.
 */
export interface IInvitation {
	invitationId: string
	tenantId: string
	/** The invited user. */
	userId: string
	/** Hex SHA-256 of the link token. Never serialized to a DTO. */
	tokenHash: string
	status: InvitationStatus
	expiresAt: Date
	/** The inviter's internal user id, if issued by a user. */
	createdBy: string | null
	createdAt: Date
	updatedAt: Date
}
